
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Court
 *
 * A judicial venue (federal, state, tribal, military, or other) where a law firm's cases are heard. Courts are used in deadline-rule configuration: each court can have one or more deadline rules assigned to it (via the courtDeadlineRule junction) so that the system knows which filing and response deadlines apply when a matter is pending before that court. Court data is modeled after the CourtListener schema to support future data import.
 */
class Court extends Base implements EntityType {
    courtId: uuid;
    name: string;
    shortName?: string | null;
    jurisdiction: string;
    courtCode?: string | null;
    state?: string | null;
    county?: string | null;
    district?: string | null;
    circuit?: string | null;
    parentCourtId?: uuid | null;
    pacerCourtId?: number | null;
    fjcCourtId?: string | null;
    citationString?: string | null;
    url?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    inUse: boolean;
    description?: string | null;
    position?: number | null;

    /**
     * Create a new Court, with an (optional) JSON object or Court class
     *
     * @param {Object|Court} doc - Can be either a simple JSON object or a Court class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Court | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            courtId: genUUID(),
            name: null,
            jurisdiction: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'court';
        // @ts-ignore
        this.courtId = doc._courtId || doc.courtId || doc.courtid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.shortName = doc._shortName || doc.shortName || doc.shortname || null;
        // @ts-ignore
        this.jurisdiction = doc._jurisdiction || doc.jurisdiction || null;
        // @ts-ignore
        this.courtCode = doc._courtCode || doc.courtCode || doc.courtcode || null;
        // @ts-ignore
        this.state = doc._state || doc.state || null;
        // @ts-ignore
        this.county = doc._county || doc.county || null;
        // @ts-ignore
        this.district = doc._district || doc.district || null;
        // @ts-ignore
        this.circuit = doc._circuit || doc.circuit || null;
        // @ts-ignore
        this.parentCourtId = doc._parentCourtId || doc.parentCourtId || doc.parentcourtid || null;
        // @ts-ignore
        this.pacerCourtId = util.coalesce(util.parseInt(doc._pacerCourtId), util.parseInt(doc.pacerCourtId), util.parseInt(doc.pacercourtid), null);
        // @ts-ignore
        this.fjcCourtId = doc._fjcCourtId || doc.fjcCourtId || doc.fjccourtid || null;
        // @ts-ignore
        this.citationString = doc._citationString || doc.citationString || doc.citationstring || null;
        // @ts-ignore
        this.url = doc._url || doc.url || null;
        // @ts-ignore
        this.startDate = DateUtil.dateOnly(doc._startDate) || DateUtil.dateOnly(doc.startDate) || DateUtil.dateOnly(doc.startdate) || null;
        // @ts-ignore
        this.endDate = DateUtil.dateOnly(doc._endDate) || DateUtil.dateOnly(doc.endDate) || DateUtil.dateOnly(doc.enddate) || null;
        // @ts-ignore
        this.inUse = util.defaultBoolean([doc.inUse, doc._inUse, doc.inuse, true]);
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.position = util.coalesce(util.parseFloat(doc._position), util.parseFloat(doc.position), null);

    }
    /**
     * @returns {uuid} This is a generic pointer to the courtId across all entity types
     */
    get id(): uuid {
        return this.courtId;
    }


    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this court"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.courtId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return [];
    }

    /**
     * Default entries are Outlaw-supplied Court entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Court[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'court',
            table: 'COURT',
            comment: `A judicial venue (federal, state, tribal, military, or other) where a law firm's cases are heard. Courts are used in deadline-rule configuration: each court can have one or more deadline rules assigned to it (via the courtDeadlineRule junction) so that the system knows which filing and response deadlines apply when a matter is pending before that court. Court data is modeled after the CourtListener schema to support future data import.`,
            fields: {
                courtId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this court'
                },
                /**
                 * Display name, e.g. "Harris County District Court"
                 */
                name: {
                    type: 'string',
                    required: true,
                    searchable: true,
                    comment: 'Display name for this court'
                },
                /**
                 * Abbreviated court name, e.g. "S.D. Tex."
                 */
                shortName: {
                    type: 'string',
                    searchable: true,
                    comment: 'Abbreviated court name'
                },
                /**
                 * CourtListener jurisdiction classification code.
                 * F=Federal Appellate, FD=Federal District, FB=Federal Bankruptcy,
                 * FBP=Federal Bankruptcy Panel, FS=Federal Special,
                 * S=State Supreme, SA=State Appellate, ST=State Trial, SS=State Special,
                 * SAG=State Attorney General,
                 * TRS/TRA/TRT/TRX=Tribal, TS/TA/TT/TSP=Territory,
                 * MA/MT=Military, C=Committee, I=International
                 */
                jurisdiction: {
                    type: 'string',
                    required: true,
                    maxLength: 4,
                    comment: 'CourtListener jurisdiction code (e.g. FD, ST, SA)'
                },
                /**
                 * Short ID matching CourtListener, e.g. "txsd"
                 */
                courtCode: {
                    type: 'string',
                    searchable: true,
                    comment: 'Short ID matching CourtListener (e.g. txsd)'
                },
                /**
                 * US state abbreviation, e.g. "TX", "CA"
                 */
                state: {
                    type: 'string',
                    minLength: 2,
                    maxLength: 2,
                    searchable: true,
                    comment: 'US state abbreviation (e.g. TX, CA)'
                },
                /**
                 * County name, e.g. "Harris", "Travis"
                 */
                county: {
                    type: 'string',
                    searchable: true,
                    comment: 'County name (e.g. Harris, Travis)'
                },
                /**
                 * Federal district, e.g. "Southern" (for federal district courts)
                 */
                district: {
                    type: 'string',
                    comment: 'Federal district (e.g. Southern, Northern)'
                },
                /**
                 * Federal circuit, e.g. "5th" (for federal appellate courts)
                 */
                circuit: {
                    type: 'string',
                    comment: 'Federal circuit (e.g. 5th, 9th)'
                },
                /**
                 * Parent court for hierarchy
                 */
                parentCourtId: {
                    type: 'uuid',
                    entityType: 'court',
                    comment: 'Parent court for hierarchy'
                },
                /**
                 * Numeric PACER court identifier
                 */
                pacerCourtId: {
                    type: 'int',
                    comment: 'Numeric PACER court identifier'
                },
                /**
                 * Federal Judicial Center identifier
                 */
                fjcCourtId: {
                    type: 'string',
                    comment: 'Federal Judicial Center identifier'
                },
                /**
                 * Blue Book citation abbreviation
                 */
                citationString: {
                    type: 'string',
                    comment: 'Blue Book citation abbreviation'
                },
                /**
                 * Court website URL
                 */
                url: {
                    type: 'string',
                    comment: 'Court website URL'
                },
                /**
                 * Date court was established
                 */
                startDate: {
                    type: 'date',
                    comment: 'Date court was established'
                },
                /**
                 * Date court was abolished
                 */
                endDate: {
                    type: 'date',
                    comment: 'Date court was abolished'
                },
                /**
                 * Whether this court is active
                 */
                inUse: {
                    type: 'boolean',
                    default: true,
                    comment: 'Whether this court is active'
                },
                /**
                 * Detailed description of the court
                 */
                description: {
                    type: 'string',
                    comment: 'Description of this court'
                },
                /**
                 * Dewey-decimal hierarchical ordering (float, matching CourtListener)
                 */
                position: {
                    type: 'float',
                    comment: 'Display position for sorting (dewey-decimal style)'
                }
            },
            indexes: [{
                name: 'notDeleted',
                fn: s => s.status !== 'D'
            }],
            maps: [{
                    name: 'jurisdiction',
                    keysFn: s => s.jurisdiction,
                    presentFn: (s, k) => s.jurisdiction === k && s.status !== 'D'
                },
                {
                    name: 'courtCode',
                    keysFn: s => s.courtCode,
                    presentFn: (s, k) => s.courtCode === k && s.status !== 'D'
                },
                {
                    name: 'state',
                    keysFn: s => s.state,
                    presentFn: (s, k) => s.state === k && s.status !== 'D'
                },
                {
                    name: 'courtId',
                    keysFn: s => s.parentCourtId,
                    presentFn: (s, k) => s.parentCourtId === k && s.status !== 'D'
                }
            ]
        });
    }

}
export default Court;
